"use client";

import { Loader2Icon, Lock, Mail, User } from "lucide-react";
import { useActionState, useEffect } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { AuthActionState } from "@/server-actions/supabase-auth/_types";
import { supabaseSignUpWithEmail } from "@/server-actions/supabase-auth/sign_up_with_email";

export function SignupForm() {
  const [state, formAction, isPending] = useActionState<
    AuthActionState,
    FormData
  >(supabaseSignUpWithEmail, null);

  useEffect(() => {
    if (state?.success) {
      toast.success(state.success);
    }
  }, [state?.success]);

  return (
    <form action={formAction}>
      <FieldGroup className="gap-5">
        <Field>
          <FieldLabel
            htmlFor="first_name"
            className="text-xs font-medium text-zinc-600 dark:text-zinc-400"
          >
            First name
          </FieldLabel>
          <div className="relative">
            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              id="first_name"
              name="first_name"
              type="text"
              placeholder="John"
              autoComplete="given-name"
              required
              disabled={isPending}
              className={cn(
                "h-12 rounded-lg border-zinc-200 bg-white pl-10 text-base",
                "placeholder:text-base placeholder:text-zinc-400",
                "focus-visible:border-primary/50 focus-visible:ring-primary/20",
                "dark:border-zinc-700 dark:bg-zinc-800/50 dark:placeholder:text-zinc-500",
              )}
            />
          </div>
        </Field>

        <Field>
          <FieldLabel
            htmlFor="email"
            className="text-xs font-medium text-zinc-600 dark:text-zinc-400"
          >
            Email
          </FieldLabel>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              required
              disabled={isPending}
              className={cn(
                "h-12 rounded-lg border-zinc-200 bg-white pl-10 text-base",
                "placeholder:text-base placeholder:text-zinc-400",
                "focus-visible:border-primary/50 focus-visible:ring-primary/20",
                "dark:border-zinc-700 dark:bg-zinc-800/50 dark:placeholder:text-zinc-500",
              )}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <FieldLabel
              htmlFor="password"
              className="text-xs font-medium text-zinc-600 dark:text-zinc-400"
            >
              Password
            </FieldLabel>
            <p className="text-xs text-zinc-400">Min. 6 characters</p>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Create a password"
              autoComplete="new-password"
              minLength={6}
              required
              disabled={isPending}
              className={cn(
                "h-12 rounded-lg border-zinc-200 bg-white pl-10 text-base",
                "placeholder:text-base placeholder:text-zinc-400",
                "focus-visible:border-primary/50 focus-visible:ring-primary/20",
                "dark:border-zinc-700 dark:bg-zinc-800/50 dark:placeholder:text-zinc-500",
              )}
            />
          </div>
        </Field>

        {state?.error && (
          <Alert variant="destructive" className="rounded-lg">
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {state?.success && (
          <Alert className="rounded-lg border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
            <AlertDescription>{state.success}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={isPending}
          className={cn(
            "h-12 w-full rounded-lg text-base font-medium",
            "bg-primary text-primary-foreground hover:bg-primary/90",
          )}
        >
          {isPending ? (
            <>
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create account"
          )}
        </Button>
      </FieldGroup>
    </form>
  );
}
